import React from 'react';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { TaskPriority } from '@demind-inc/core';
import FlagIcon from '@mui/icons-material/Flag';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { mdiUnfoldMoreHorizontal } from '@mdi/js';
import Icon from '@mdi/react';
import { getPriorityColor } from '../helpers';
import './PrioritySelect.scss';

interface PrioritySelectProps {
  value?: TaskPriority;
  onChange: (priority?: TaskPriority) => void;
  className?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

const getOptions = (size: 'small' | 'medium') => [
  {
    value: 'high' as TaskPriority,
    labelKey: 'task.filters.priorities.high',
    icon: <FlagIcon fontSize={size} />,
  },
  {
    value: 'medium' as TaskPriority,
    labelKey: 'task.filters.priorities.medium',
    icon: <FlagIcon fontSize={size} />,
  },
  {
    value: 'low' as TaskPriority,
    labelKey: 'task.filters.priorities.low',
    icon: <FlagIcon fontSize={size} />,
  },
];

export const PrioritySelect: React.FC<PrioritySelectProps> = ({
  value,
  onChange,
  className,
  size = 'small',
  disabled,
}) => {
  const { t } = useTranslation();
  const options = getOptions(size);

  const handleChange = (e: SelectChangeEvent<string>) => {
    const v = e.target.value || undefined;
    onChange(v as TaskPriority | undefined);
  };

  return (
    <Select
      className={clsx('priority-select', className)}
      size={size}
      value={value ?? ''}
      onChange={handleChange}
      displayEmpty
      disabled={disabled}
      IconComponent={() => (
        <Icon path={mdiUnfoldMoreHorizontal} size="16px" className="priority-select__icon" />
      )}
      renderValue={(selected) => {
        const found = options.find((o) => (o.value ?? '') === selected);
        const isEmpty = !selected || !found;
        return (
          <div
            className={clsx('priority-select__value', isEmpty && 'priority-select__value--empty')}
            style={isEmpty ? undefined : { color: getPriorityColor(found.value) }}
          >
            <span>{found ? t(found.labelKey) : t('task.filters.priority')}</span>
          </div>
        );
      }}
      MenuProps={{
        PaperProps: {
          className: 'priority-select__menu-paper',
        },
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value ?? ''} className={'priority-select__item'}>
          <div
            className={`priority-select__item-content priority-select__item--${opt.value || 'none'}`}
          >
            {opt.icon}
            <span className={'priority-select__item__label'}>{t(opt.labelKey)}</span>
          </div>
        </MenuItem>
      ))}
    </Select>
  );
};

export default PrioritySelect;
